// Bilingual content for the Terms of Service page (src/components/Terms.tsx)

export interface TermsContent {
  backLabel: string;
  pageTitle: string;
  lastUpdatedLabel: string;
  lastUpdatedDate: string;

  // 1. Acceptance of terms
  section1Title: string;
  section1Body: string;

  // 2. Service description
  section2Title: string;
  section2Intro: string;
  section2Items: string[];
  section2Body2: string;

  // 3. User account
  section3Title: string;
  section3Intro: string;
  section3Items: string[];
  section3NoticeLabel: string;
  section3NoticeBody: string;

  // 4. Premium subscription
  section4Title: string;
  section4_1Title: string;
  section4_1Body: string;
  section4_2Title: string;
  section4_2Body: string;
  section4_3Title: string;
  section4_3Body: string;
  section4_3NoticeLabel: string;
  section4_3NoticeBody: string;

  // 5. Prohibited use
  section5Title: string;
  section5Intro: string;
  section5Items: string[];

  // 6. Intellectual property
  section6Title: string;
  section6Body: string;

  // 7. Personal data
  section7Title: string;
  section7BodyBefore: string;
  privacyLinkText: string;
  section7BodyAfter: string;

  // 8. Service availability
  section8Title: string;
  section8Body: string;

  // 9. Limitation of liability
  section9Title: string;
  section9Body: string;

  // 10. Account termination
  section10Title: string;
  section10Body: string;

  // 11. Changes to terms
  section11Title: string;
  section11Body: string;

  // 12. Governing law
  section12Title: string;
  section12Body: string;

  // 13. Contact
  section13Title: string;
  contactEmailLabel: string;
  contactEmail: string;
  contactAppLabel: string;
  contactAppName: string;
}

export const TERMS_CONTENT: Record<'fr' | 'en', TermsContent> = {
  fr: {
    backLabel: 'Retour',
    pageTitle: "Conditions générales d'utilisation",
    lastUpdatedLabel: 'Dernière mise à jour :',
    lastUpdatedDate: '16 mars 2026',

    section1Title: '1. Acceptation des conditions',
    section1Body:
      "En accédant et en utilisant Jogolinga, vous acceptez d'être lié par les présentes conditions générales d'utilisation. " +
      "Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser notre service.",

    section2Title: '2. Description du service',
    section2Intro:
      "Jogolinga est une plateforme interactive dédiée à l'apprentissage des langues africaines. " +
      "Le service comprend :",
    section2Items: [
      'Des exercices de vocabulaire et de grammaire par catégories',
      'Un module de construction de phrases interactif',
      'Des mini-jeux pédagogiques (mémoire, association, etc.)',
      'Un système de progression par niveaux (12 niveaux, de « Initié » à « Œil d\'Horus »)',
      'Un système de séries quotidiennes (streak) pour encourager la régularité',
      'Un classement communautaire (leaderboard) entre utilisateurs',
      'Une offre Premium avec des fonctionnalités avancées',
    ],
    section2Body2:
      "La connexion s'effectue exclusivement via un compte Google. La progression est sauvegardée dans le cloud via Supabase.",

    section3Title: '3. Compte utilisateur',
    section3Intro: 'Pour utiliser Jogolinga, vous devez :',
    section3Items: [
      "Être âgé d'au moins 13 ans (ou l'âge minimum légal dans votre pays)",
      "Disposer d'un compte Google valide",
      'Fournir des informations exactes et à jour',
      'Utiliser le service uniquement à des fins éducatives légitimes',
    ],
    section3NoticeLabel: 'Visibilité publique :',
    section3NoticeBody:
      "votre pseudo, votre total d'XP et votre série quotidienne sont visibles " +
      "par les autres utilisateurs dans le classement. Vous pouvez choisir un pseudo distinct de votre nom Google " +
      "depuis les paramètres de votre profil.",

    section4Title: '4. Abonnement Premium',
    section4_1Title: '4.1 Tarification et paiement',
    section4_1Body:
      "Les tarifs de l'abonnement Premium sont affichés dans l'application. " +
      "Le paiement est traité de manière sécurisée par notre prestataire de paiement. " +
      "En souscrivant, vous autorisez Jogolinga à facturer le montant correspondant " +
      "selon la périodicité choisie (mensuelle ou annuelle).",
    section4_2Title: '4.2 Renouvellement automatique',
    section4_2Body:
      "L'abonnement est renouvelé automatiquement à la fin de chaque période, " +
      "sauf résiliation de votre part avant la date de renouvellement.",
    section4_3Title: '4.3 Résiliation et remboursement',
    section4_3Body:
      "Vous pouvez résilier votre abonnement à tout moment depuis votre profil. " +
      "La résiliation prend effet à la fin de la période en cours.",
    section4_3NoticeLabel: 'Droit de rétractation :',
    section4_3NoticeBody:
      "conformément à l'article L221-28 du Code de la consommation, " +
      "le droit de rétractation de 14 jours ne s'applique pas aux services numériques dont l'exécution a commencé " +
      "avec votre accord exprès avant l'expiration du délai. " +
      "En commençant à utiliser Jogolinga Premium immédiatement après la souscription, vous reconnaissez renoncer " +
      "à ce droit de rétractation.",

    section5Title: '5. Utilisation interdite',
    section5Intro: 'Vous vous engagez à ne pas :',
    section5Items: [
      'Utiliser le service à des fins illégales ou frauduleuses',
      'Diffuser du contenu inapproprié, offensant ou discriminatoire',
      "Violer des droits d'auteur ou marques déposées",
      'Télécharger des virus ou interférer avec la sécurité du service',
      "Harceler ou menacer d'autres utilisateurs",
      'Redistribuer ou commercialiser les contenus sans autorisation écrite de Jogolinga',
      'Utiliser des procédés automatisés (bots, scripts) pour manipuler le classement ou la progression',
      "Tenter de contourner les restrictions de l'offre gratuite",
    ],

    section6Title: '6. Propriété intellectuelle',
    section6Body:
      "Tous les contenus et fonctionnalités de Jogolinga — exercices, données linguistiques, niveaux de progression, " +
      "mini-jeux et interface — sont protégés par les lois applicables en matière de droits d'auteur et de propriété intellectuelle, " +
      "et restent la propriété exclusive de Jogolinga et de ses concédants.",

    section7Title: '7. Données personnelles',
    section7BodyBefore: 'La collecte et le traitement de vos données personnelles sont décrits en détail dans notre',
    privacyLinkText: 'Politique de Confidentialité',
    section7BodyAfter: '. En utilisant Jogolinga, vous acceptez les pratiques de traitement des données qui y sont décrites.',

    section8Title: '8. Disponibilité du service',
    section8Body:
      "Nous faisons de notre mieux pour assurer la disponibilité continue du service, " +
      "mais nous ne pouvons pas garantir une accessibilité sans interruption. " +
      "Des maintenances ou incidents techniques peuvent limiter temporairement l'accès.",

    section9Title: '9. Limitation de responsabilité',
    section9Body:
      "Dans les limites autorisées par la loi, Jogolinga ne peut être tenu responsable des dommages " +
      "indirects ou consécutifs liés à l'utilisation du service, notamment la perte de données de progression " +
      "en cas de suppression volontaire du compte ou de défaillance technique. " +
      "Notre responsabilité ne pourra excéder le montant payé pour le service durant les 12 mois précédents.",

    section10Title: '10. Résiliation du compte',
    section10Body:
      "Vous pouvez supprimer votre compte à tout moment depuis votre profil. " +
      "La suppression entraîne l'effacement définitif et irréversible de toutes vos données de progression " +
      "et de votre abonnement Premium actif. " +
      "Nous nous réservons le droit de suspendre ou de résilier un compte en cas de non-respect des présentes conditions.",

    section11Title: '11. Modifications des conditions',
    section11Body:
      "Nous pouvons modifier ces conditions à tout moment. En cas de modification substantielle, " +
      "vous en serez informé via l'application ou par e-mail. " +
      "La poursuite de l'utilisation du service après notification vaut acceptation des nouvelles conditions.",

    section12Title: '12. Droit applicable',
    section12Body:
      "Les présentes conditions sont régies par le droit français. " +
      "Tout litige sera soumis à la compétence exclusive des tribunaux français.",

    section13Title: '13. Contact',
    contactEmailLabel: 'Email :',
    contactEmail: 'jogolinga@gmail.com',
    contactAppLabel: 'Application :',
    contactAppName: 'Jogolinga',
  },
  en: {
    backLabel: 'Back',
    pageTitle: 'General Terms and Conditions of Use',
    lastUpdatedLabel: 'Last updated:',
    lastUpdatedDate: 'March 16, 2026',

    section1Title: '1. Acceptance of the terms',
    section1Body:
      "By accessing and using Jogolinga, you agree to be bound by these General Terms and Conditions of Use. " +
      "If you do not agree to these terms, you must not use our Service.",

    section2Title: '2. Description of the Service',
    section2Intro:
      "Jogolinga is an interactive platform dedicated to learning African languages. " +
      "The Service includes:",
    section2Items: [
      'Vocabulary and grammar exercises organized by category',
      'An interactive sentence-building module',
      'Educational mini-games (memory, matching, etc.)',
      'A level-based progression system (12 levels, from "Initiate" to "Eye of Horus")',
      'A daily streak system to encourage regular practice',
      'A community leaderboard among users',
      'A Premium offering with advanced features',
    ],
    section2Body2:
      "Sign-in is available exclusively via a Google account. Progress is saved to the cloud via Supabase.",

    section3Title: '3. User account',
    section3Intro: 'To use Jogolinga, you must:',
    section3Items: [
      'Be at least 13 years old (or the legal minimum age in your country)',
      'Have a valid Google account',
      'Provide accurate and up-to-date information',
      'Use the Service solely for legitimate educational purposes',
    ],
    section3NoticeLabel: 'Public visibility:',
    section3NoticeBody:
      "your username, your total XP, and your daily streak are visible " +
      "to other users on the leaderboard. You may choose a username different from your Google name " +
      "from your profile settings.",

    section4Title: '4. Premium subscription',
    section4_1Title: '4.1 Pricing and payment',
    section4_1Body:
      "Premium subscription pricing is displayed within the application. " +
      "Payment is processed securely by our payment provider. " +
      "By subscribing, you authorize Jogolinga to charge the corresponding amount " +
      "according to the billing period you selected (monthly or annual).",
    section4_2Title: '4.2 Automatic renewal',
    section4_2Body:
      "The subscription renews automatically at the end of each period, " +
      "unless you cancel before the renewal date.",
    section4_3Title: '4.3 Cancellation and refunds',
    section4_3Body:
      "You may cancel your subscription at any time from your profile. " +
      "Cancellation takes effect at the end of the current billing period.",
    section4_3NoticeLabel: 'Right of withdrawal:',
    section4_3NoticeBody:
      "in accordance with Article L221-28 of the French Consumer Code, " +
      "the 14-day right of withdrawal does not apply to digital services whose performance has begun " +
      "with your express consent before the withdrawal period has expired. " +
      "By starting to use Jogolinga Premium immediately after subscribing, you acknowledge that you waive " +
      "this right of withdrawal.",

    section5Title: '5. Prohibited use',
    section5Intro: 'You agree not to:',
    section5Items: [
      'Use the Service for illegal or fraudulent purposes',
      'Distribute inappropriate, offensive, or discriminatory content',
      'Infringe copyrights or registered trademarks',
      "Upload viruses or interfere with the Service's security",
      'Harass or threaten other users',
      "Redistribute or commercialize the content without Jogolinga's written authorization",
      'Use automated means (bots, scripts) to manipulate the leaderboard or progress data',
      'Attempt to circumvent the restrictions of the free tier',
    ],

    section6Title: '6. Intellectual property',
    section6Body:
      "All content and features of Jogolinga — exercises, linguistic data, progression levels, " +
      "mini-games, and interface — are protected by applicable copyright and intellectual property laws, " +
      "and remain the exclusive property of Jogolinga and its licensors.",

    section7Title: '7. Personal data',
    section7BodyBefore: 'The collection and processing of your personal data are described in detail in our',
    privacyLinkText: 'Privacy Policy',
    section7BodyAfter: '. By using Jogolinga, you agree to the data processing practices described therein.',

    section8Title: '8. Service availability',
    section8Body:
      "We do our best to ensure the continuous availability of the Service, " +
      "but we cannot guarantee uninterrupted access. " +
      "Maintenance or technical incidents may temporarily limit access.",

    section9Title: '9. Limitation of liability',
    section9Body:
      "To the extent permitted by law, Jogolinga shall not be held liable for any indirect " +
      "or consequential damages related to the use of the Service, including the loss of progress data " +
      "resulting from voluntary account deletion or technical failure. " +
      "Our liability shall not exceed the amount paid for the Service during the preceding 12 months.",

    section10Title: '10. Account termination',
    section10Body:
      "You may delete your account at any time from your profile. " +
      "Deletion results in the permanent and irreversible erasure of all your progress data " +
      "and any active Premium subscription. " +
      "We reserve the right to suspend or terminate an account in the event of non-compliance with these terms.",

    section11Title: '11. Changes to the terms',
    section11Body:
      "We may modify these terms at any time. In the event of a substantial change, " +
      "you will be notified via the application or by email. " +
      "Continued use of the Service after notification constitutes acceptance of the new terms.",

    section12Title: '12. Governing law',
    section12Body:
      "These terms are governed by French law. " +
      "Any dispute shall be subject to the exclusive jurisdiction of the French courts.",

    section13Title: '13. Contact',
    contactEmailLabel: 'Email:',
    contactEmail: 'jogolinga@gmail.com',
    contactAppLabel: 'Application:',
    contactAppName: 'Jogolinga',
  },
};
